import { createSlice } from '@reduxjs/toolkit';
import { IS_NORWAY } from '@commons/appType';
// Cache for parsed countdown data to avoid re-evaluating the same HTML strings
const countdownCache = new Map();
const initialState = {
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
    isRunning: false,
    isExpired: false,
    deadline: '2023-08-25T06:50:00+00:00',
    endAction: 0,
    flip: 0,
    id: IS_NORWAY ? 'hc_cd_p9062' : 'hc_cd_p3158',
    heading: IS_NORWAY ? 'SISTE SJANSE!' : 'SISTA CHANSEN!',
    subheading: '',
};
// Helper function to load and cache countdown data from HTML string
const loadProductCountdown = (htmlString, productData) => {
    // Check cache first
    const cacheKey = `${productData.variant_id}-${productData.product_id}`;
    if (countdownCache.has(cacheKey)) {
        return countdownCache.get(cacheKey);
    }
    // Extract hc_product_countdown_dates directly from htmlString since jQuery strips script tags
    let hc_product_countdown_dates = null;
    const scriptRegex = /<script[^>]*id=["']hc_product_countdown_dates["'][^>]*>([\s\S]*?)<\/script>/i;
    const scriptMatch = htmlString.match(scriptRegex);
    if (scriptMatch && scriptMatch[1]) {
        const scriptContent = scriptMatch[1].trim();
        try {
            // Parse the entire script content as JSON since it's type="application/json"
            hc_product_countdown_dates = JSON.parse(scriptContent);
        }
        catch (e) {
            console.warn('Failed to parse hc_product_countdown_dates JSON:', e);
        }
    }
    // Extract hcCountdownSettings from regular script tags
    let hcCountdownSettings = null;
    const hcScriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
    let hcScriptMatch;
    while ((hcScriptMatch = hcScriptRegex.exec(htmlString)) !== null) {
        const scriptContent = hcScriptMatch[1];
        if (scriptContent.includes('hcCountdownSettings')) {
            // Find the start of the variable assignment
            const varStart = scriptContent.indexOf('hcCountdownSettings =');
            if (varStart !== -1) {
                // Find the opening brace after the assignment
                const braceStart = scriptContent.indexOf('{', varStart);
                if (braceStart !== -1) {
                    // Count braces to find the matching closing brace
                    let braceCount = 0;
                    let braceEnd = braceStart;
                    for (let i = braceStart; i < scriptContent.length; i++) {
                        if (scriptContent[i] === '{') {
                            braceCount++;
                        }
                        else if (scriptContent[i] === '}') {
                            braceCount--;
                            if (braceCount === 0) {
                                braceEnd = i;
                                break;
                            }
                        }
                    }
                    if (braceCount === 0) {
                        // Extract the object content
                        const objectContent = scriptContent.substring(braceStart, braceEnd + 1);
                        try {
                            // Use Function constructor to safely parse JavaScript object
                            hcCountdownSettings = new Function(`return (${objectContent})`)();
                            break; // Found it, no need to continue searching
                        }
                        catch (e) {
                            console.warn('Object content that failed:', objectContent === null || objectContent === void 0 ? void 0 : objectContent.substring(0, 200));
                        }
                    }
                }
            }
            else {
                console.warn('hcCountdownSettings found but no " = " assignment');
            }
        }
    }
    hc_product_countdown_dates = hc_product_countdown_dates !== null && hc_product_countdown_dates !== void 0 ? hc_product_countdown_dates : {};
    const collectionDates = Object.keys(hc_product_countdown_dates)
        .filter((id) => {
        var _a, _b, _c;
        return (((_a = productData.variant_id) === null || _a === void 0 ? void 0 : _a.toString()) == id ||
            ((_b = productData.product_id) === null || _b === void 0 ? void 0 : _b.toString()) == id ||
            ((_c = productData.collections) === null || _c === void 0 ? void 0 : _c.some((col) => { var _a; return id === ((_a = col.id) === null || _a === void 0 ? void 0 : _a.toString()); })));
    })
        .map((id) => hc_product_countdown_dates[id])
        .filter((date) => { var _a, _b; return ((_a = date.sale_start) === null || _a === void 0 ? void 0 : _a.length) && ((_b = date.sale_end) === null || _b === void 0 ? void 0 : _b.length); });
    const result = {
        collectionDates,
        hcCountdownSettings,
    };
    // Cache the result
    countdownCache.set(cacheKey, result);
    return result;
};
// Helper function to calculate countdown values
const calculateCountdown = (saleData) => {
    const now = new Date().getTime();
    const saleEnd = new Date(saleData.sale_end).getTime();
    const timeLeft = saleEnd - now;
    if (timeLeft <= 0) {
        return { days: '00', hours: '00', minutes: '00', seconds: '00', isExpired: true };
    }
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
        .toString()
        .padStart(2, '0');
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        .toString()
        .padStart(2, '0');
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
        .toString()
        .padStart(2, '0');
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)
        .toString()
        .padStart(2, '0');
    return { days, hours, minutes, seconds, isExpired: false };
};
export const countdownSlice = createSlice({
    name: 'productCountdown',
    initialState,
    reducers: {
        loadCountdownFromHtml: (state, action) => {
            var _a, _b;
            const countdownValue = loadProductCountdown(action.payload.htmlString, action.payload.productData);
            const data = (_b = (_a = countdownValue === null || countdownValue === void 0 ? void 0 : countdownValue.collectionDates) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null;
            if (data != null) {
                state.countdownData = data;
                state.isExpired = false;
                const result = calculateCountdown(data);
                state.days = result.days;
                state.hours = result.hours;
                state.minutes = result.minutes;
                state.seconds = result.seconds;
                state.isExpired = result.isExpired;
                state.isRunning = !result.isExpired;
            }
            else {
                // No countdown data found - clear countdown
                state.countdownData = null;
                state.isRunning = false;
                state.isExpired = false;
                state.days = '00';
                state.hours = '00';
                state.minutes = '00';
                state.seconds = '00';
            }
        },
        startCountdown: (state) => {
            state.isRunning = true;
        },
        stopCountdown: (state) => {
            state.isRunning = false;
        },
        expireCountdown: (state) => {
            state.isExpired = true;
            state.isRunning = false;
            state.days = '00';
            state.hours = '00';
            state.minutes = '00';
            state.seconds = '00';
        },
        resetCountdown: (state) => {
            Object.assign(state, initialState);
        },
        initializeCountdown: (state, action) => {
            state.countdownData = action.payload;
            state.isExpired = false;
            const result = calculateCountdown(action.payload);
            state.days = result.days;
            state.hours = result.hours;
            state.minutes = result.minutes;
            state.seconds = result.seconds;
            state.isExpired = result.isExpired;
            state.isRunning = !result.isExpired;
        },
        updateCountdown: (state) => {
            if (state.countdownData != null && state.isRunning && !state.isExpired) {
                const result = calculateCountdown(state.countdownData);
                state.days = result.days;
                state.hours = result.hours;
                state.minutes = result.minutes;
                state.seconds = result.seconds;
                if (result.isExpired) {
                    state.isExpired = true;
                    state.isRunning = false;
                }
            }
        },
        setStaticCountdown: (state, action) => {
            state.days = action.payload.days;
            state.hours = action.payload.hours;
            state.minutes = action.payload.minutes;
            state.seconds = action.payload.seconds;
            state.isRunning = false;
            state.isExpired = false;
            state.countdownData = undefined;
        },
        setDisplayConfig: (state, action) => {
            if (action.payload.deadline !== undefined)
                state.deadline = action.payload.deadline;
            if (action.payload.endAction !== undefined)
                state.endAction = action.payload.endAction;
            if (action.payload.flip !== undefined)
                state.flip = action.payload.flip;
            if (action.payload.id !== undefined)
                state.id = action.payload.id;
            if (action.payload.heading !== undefined)
                state.heading = action.payload.heading;
            if (action.payload.subheading !== undefined)
                state.subheading = action.payload.subheading;
        },
        updateCountdownData: (state, action) => {
            state.countdownData = action.payload;
            state.isExpired = false;
            const result = calculateCountdown(action.payload);
            state.days = result.days;
            state.hours = result.hours;
            state.minutes = result.minutes;
            state.seconds = result.seconds;
            state.isExpired = result.isExpired;
            state.isRunning = !result.isExpired;
        },
        removeCountdown: (state) => {
            state.countdownData = undefined;
            state.isRunning = false;
            state.isExpired = false;
            state.days = '00';
            state.hours = '00';
            state.minutes = '00';
            state.seconds = '00';
        },
        updateDisplayAndCountdown: (state, action) => {
            // Update display config if provided
            if (action.payload.display) {
                if (action.payload.display.deadline !== undefined)
                    state.deadline = action.payload.display.deadline;
                if (action.payload.display.endAction !== undefined)
                    state.endAction = action.payload.display.endAction;
                if (action.payload.display.flip !== undefined)
                    state.flip = action.payload.display.flip;
                if (action.payload.display.id !== undefined)
                    state.id = action.payload.display.id;
                if (action.payload.display.heading !== undefined)
                    state.heading = action.payload.display.heading;
                if (action.payload.display.subheading !== undefined)
                    state.subheading = action.payload.display.subheading;
            }
            // Update countdown data if provided
            if (action.payload.countdownData != null) {
                state.countdownData = action.payload.countdownData;
                state.isExpired = false;
                const result = calculateCountdown(action.payload.countdownData);
                state.days = result.days;
                state.hours = result.hours;
                state.minutes = result.minutes;
                state.seconds = result.seconds;
                state.isExpired = result.isExpired;
                state.isRunning = !result.isExpired;
            }
            else if (action.payload.countdownData === null) {
                // Explicitly set to null - clear countdown
                state.countdownData = null;
                state.isRunning = false;
                state.isExpired = false;
                state.days = '00';
                state.hours = '00';
                state.minutes = '00';
                state.seconds = '00';
            }
        },
    },
});
export const { loadCountdownFromHtml, startCountdown, stopCountdown, expireCountdown, resetCountdown, initializeCountdown, updateCountdown, setDisplayConfig, updateCountdownData, removeCountdown, updateDisplayAndCountdown, } = countdownSlice.actions;
export default countdownSlice.reducer;
